import { Flex } from 'antd';
import CardDetailButton from '../cardDetailButton/cardDetailButton';
import InteractiveCard from '../common/interactiveCard';
import { Divider } from '../Ant';
import { LampOn, ProfileRemove, Receive, Share, Verify } from '../icons';
import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { ProfileUser } from '../icons/ProfileUser';
import { IConflictSearch } from '../../interface/conflictSearch';
import { ROUTES } from '../../constants/constants';
import {
  displayRoles,
  displayType,
  getConflictSearchInformativeIcon,
} from '../../constants/conflictSearchUtility';
import { displayFullCountryName } from '../../constants/utilityFunctions';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ICustomerType } from '../../constants/customer';
import { BorderColor, TagColor } from '../../interface/common';
import { ProductName } from '../../interface/system';
import { useSystemInfo } from '../../context/SystemInfoContext';

interface IConflictSearchParameter {
  matterRequestInUse: boolean;
  opportunityInUse: boolean;
  blackListInUse: boolean;
  conflictSearch: IConflictSearch;
}
const ConflictCard = ({
  conflictSearch,
  matterRequestInUse,
  opportunityInUse,
  blackListInUse,
}: IConflictSearchParameter) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { language } = useLanguage();
  const { systemParams } = useSystemInfo();
  const handleGoToConflictSearch = () => {
    push(`${ROUTES.CONFLICT_SEARCH}/${conflictSearch.id}`);
  };
  const showMatterRequests =
    matterRequestInUse && systemParams?.systemInfo?.productName !== ProductName.LAKIMIES;
  return (
    <InteractiveCard
      data-testid="conflict-card"
      className={`pointer w-full conflict-card ${conflictSearch?.type === ICustomerType.COMPANY ? BorderColor.GREEN : BorderColor.BEIGE}`}
      onActivate={handleGoToConflictSearch}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={8} align="center">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${conflictSearch?.type === ICustomerType.COMPANY ? TagColor.GREEN : TagColor.BEIGE}`}
          >
            {displayType(conflictSearch.type, t)}
          </Flex>
          {getConflictSearchInformativeIcon({
            t,
            hasActiveConflictChecks: conflictSearch.hasActiveConflictChecks,
            hasMatterRequests: showMatterRequests && conflictSearch.hasMatterRequests,
            isBlackListed: conflictSearch.isBlackListed,
            hasOppositionRole: conflictSearch.hasOppositionRole,
            hasCoPartyRole: conflictSearch.hasCoPartyRole,
          })}
        </Flex>
        <Flex gap={10}>
          <CardDetailButton onOpen={handleGoToConflictSearch} testId="conflict-card-go-detail" />
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          type="matter"
          icon={<ProfileUser />}
          title={t('customer:customer')}
          content={conflictSearch?.name ?? ''}
          subcontent={displayFullCountryName(conflictSearch?.homeCountry, language) ?? ''}
          contentclass="lineclamp"
        />
        <CardData
          icon={<Share />}
          type="transaction"
          title={t('matter:roles')}
          content={displayRoles(
            conflictSearch?.hasOtherRole,
            conflictSearch?.hasOppositionRole,
            conflictSearch?.hasCoPartyRole,
            t,
          )}
        />
        <Flex justify="space-between">
          {showMatterRequests && (
            <CardData
              type="price"
              icon={<Receive />}
              title={t('matterRequest:matterRequest')}
              content={conflictSearch?.hasMatterRequests ? t('common:yes') : t('common:no')}
            />
          )}
          <CardData
            type="hours"
            icon={<Verify />}
            title={t('conflict:activeConflictCheck')}
            content={conflictSearch?.hasActiveConflictChecks ? t('common:yes') : t('common:no')}
          />
        </Flex>
        <Flex justify="space-between">
          {opportunityInUse && (
            <CardData
              type="price"
              icon={<LampOn />}
              title={t('conflict:opportunity')}
              content={conflictSearch?.hasActiveOpportunities ? t('common:yes') : t('common:no')}
            />
          )}
          {blackListInUse && (
            <CardData
              type="hours"
              icon={<ProfileRemove />}
              title={t('conflict:blacklisted')}
              content={conflictSearch?.isBlackListed ? t('common:yes') : t('common:no')}
            />
          )}
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};

export default ConflictCard;
